/**
 * API client.
 *
 * Cookie-based, same-origin. No token is ever held in JavaScript: the session
 * cookie is HttpOnly, so an XSS bug cannot read it and post it elsewhere. That
 * is the whole reason the API uses cookies rather than a bearer token in
 * localStorage, and it is worth not undoing later for convenience.
 */

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // Sends the session cookie. Without it every request is anonymous and the
    // whole app 401s in a way that looks like an auth bug rather than a fetch
    // configuration mistake.
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'error' in body
        ? String(body.error)
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PATCH', body: data ? JSON.stringify(data) : undefined }),
};

// --- Types mirrored from the API -------------------------------------------

export interface Me {
  userId: string;
  tenantId: string;
  email: string;
  name: string;
  permissions: string[];
  departmentIds: string[];
}

export interface CaseSummary {
  id: string;
  case_number: string;
  case_type: string;
  status: string;
  priority: string | null;
  ops_stage_key: string | null;
  qc_stage_key: string | null;
  billing_stage_key: string | null;
  requested_at: string;
  from_address: string | null;
  to_address: string | null;
  department_name: string | null;
  client_name: string | null;
}

export interface GuardViolation {
  guardType: string;
  message: string;
  detail?: Record<string, unknown>;
}

export interface TransitionEvaluation {
  transitionKey: string;
  label: string;
  toStageKey: string;
  allowed: boolean;
  requiresReason: boolean;
  confirmPrompt: string | null;
  violations: GuardViolation[];
  viaOverride: boolean;
}

export interface CasePipeline {
  pipeline: string;
  pipeline_name: string;
  stage: string;
  stage_name: string;
  colour: string | null;
  entered_at: string;
  sla_due_at: string | null;
  status: string;
}

export interface CaseDetail {
  case: Record<string, unknown> & { case_number: string; case_type: string; status: string };
  detail: Record<string, unknown>;
  parties: Array<{ party_type: string; name: string | null; phone_raw: string | null }>;
  assignments: Array<{
    id: string;
    role: string;
    assigned_at: string;
    released_at: string | null;
    staff_id: string | null;
    vehicle_id: string | null;
    first_name: string | null;
    last_name: string | null;
    fleet_no: string | null;
  }>;
  pipelines: CasePipeline[];
  timeline: Array<{
    event_type: string;
    occurred_at: string;
    source: string;
    data: Record<string, unknown>;
  }>;
}

export interface Vehicle {
  id: string;
  fleet_no: string;
  registration: string | null;
  vehicle_type: string;
  capability_tier: string | null;
  on_case: boolean;
}

export interface Staff {
  id: string;
  first_name: string;
  last_name: string;
  capability: string | null;
  on_case: boolean;
}

export interface ClientSummary {
  id: string;
  client_type: string;
  legal_name: string;
  trading_name: string | null;
  membership_number: string | null;
  payment_terms: string;
  is_executive: boolean;
}

export interface RenderedMessage {
  templateKey: string;
  name: string;
  channel: string;
  subject: string | null;
  text: string;
  /** Placeholders that resolved to nothing, so the UI can warn before sending. */
  missing: string[];
}

/**
 * The autosave payload. Every field optional: this fires mid-sentence while a
 * call-taker is still on the phone.
 */
// A type alias, not an interface: useAutosave constrains its payload to
// Record<string, unknown>, and an interface has no implicit index signature.
export type CasePatch = {
  priority?: string;
  clientId?: string;
  fromAddress?: string;
  fromLatitude?: string;
  fromLongitude?: string;
  fromPlaceId?: string;
  toAddress?: string;
  toFacilityId?: string;
  externalRef?: string;
  // OCC_STAT reporting fields — real columns, because the monthly board report
  // groups by every one of them.
  callSourceId?: string;
  projectNumber?: string;
  natureOfIncident?: string;
  serviceType?: string;
  servicedBy?: string;
  transportedByProvider?: boolean;
  spActivationReason?: string;
  billable?: 'yes' | 'no' | 'indigent' | '';
  detail?: Record<string, unknown>;
  onSceneContact?: { name?: string; phone?: string };
};

/** One option in a tenant-configurable dropdown. */
export interface ReferenceItem {
  key: string;
  label: string;
  systemKey: string | null;
}

export interface CallSource {
  id: string;
  key: string;
  name: string;
  project_number: string | null;
  channel: string;
  client_id: string | null;
}

export interface NearestVehicle {
  id: string;
  fleet_no: string;
  registration: string | null;
  vehicle_type: string;
  capability_tier: string | null;
  latitude: string;
  longitude: string;
  ignition: boolean | null;
  speed_kph: number | null;
  place_description: string | null;
  fix_at: string;
  fix_age_minutes: number;
  stale: boolean;
  distance_km: string;
  on_another_case: boolean;
}

export interface NearestVehicles {
  vehicles: NearestVehicle[];
  origin: { latitude: number; longitude: number } | null;
  reason?: string;
  staleAfterMinutes: number;
  /** Straight-line distance. Never present it as an ETA. */
  distanceIsStraightLine: boolean;
}

export interface CallerMatch {
  kind: 'client' | 'client_contact' | 'patient' | 'staff' | 'recent_case';
  /** Never a patient name — recognising a caller is not reading their chart. */
  label: string;
  clientId?: string;
  patientId?: string;
  caseId?: string;
  detail?: string;
}

export interface CallScreenPop {
  call: {
    id: string;
    direction: string;
    status: string;
    from_e164: string | null;
    did_e164: string | null;
    case_id: string | null;
    started_at: string;
  };
  did?: { label: string; colour: string; isEmergency: boolean };
  matches: CallerMatch[];
  openCaseIds: string[];
  history: Array<{ started_at: string; direction: string; case_number: string | null }>;
}

export interface CallerProfile {
  phone: string;
  matches: CallerMatch[];
  openCase: CallerCase | null;
  openCases: CallerCase[];
  cases: CallerCase[];
  calls: Array<{
    id: string;
    direction: string;
    status: string;
    started_at: string;
    talk_seconds: number | null;
    case_number: string | null;
    has_recording: boolean;
  }>;
  summary: { totalCases: number; totalCalls: number; firstSeen: string | null };
}

export interface CallerCase {
  id: string;
  case_number: string;
  case_type: string;
  status: string;
  ops_stage_key: string | null;
  requested_at: string;
  completed_at: string | null;
  transported: boolean | null;
  client_name: string | null;
}
